import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';

/**
 * Whether the signed-in session can still write (a session exists and its access token has not
 * expired). Used before the final save on unmount: after sign-out or session expiry the write
 * would only fail.
 * @returns {Promise<boolean>} True when a save can be attempted.
 */
export async function hasActiveSession() {
  try {
    const { data } = await supabase.auth.getSession();
    const session = data?.session;
    if (!session?.access_token) return false;
    return !session.expires_at || session.expires_at * 1000 > Date.now();
  } catch {
    return false;
  }
}

/** First retry delay after a failed save (ms); offline retries start sooner. */
export const AUTOSAVE_RETRY_BASE_MS = 8000;
const AUTOSAVE_OFFLINE_RETRY_MS = 5000;
/** Longest wait between retries (ms). Transient failures keep retrying at this ceiling indefinitely. */
export const AUTOSAVE_RETRY_MAX_MS = 60000;

/**
 * Delay before retry number `attempt` (1-based): exponential backoff with a cap.
 * @param {number} attempt - Consecutive failures so far.
 * @param {boolean} offline - Whether the browser reports offline.
 * @returns {number} Delay in ms.
 */
export function autosaveRetryDelay(attempt, offline = false) {
  const base = offline ? AUTOSAVE_OFFLINE_RETRY_MS : AUTOSAVE_RETRY_BASE_MS;
  return Math.min(AUTOSAVE_RETRY_MAX_MS, base * 2 ** Math.max(0, attempt - 1));
}

/**
 * Debounced autosave with status, retry, Mod-S support, an unload guard and a best-effort final
 * save when the page unmounts.
 *
 * The hook never reads content itself: `getContent()` is called at save time (so the editor
 * stays uncontrolled) and `save(content)` performs the single conflict-safe write.
 *
 * Saves are serialized: a save waits for the one in flight and is skipped when the content
 * already matches what was saved, so overlapping triggers (debounce, Mod-S, route blocker,
 * logout flush, unmount) never write the same content twice.
 *
 * On unmount (any reason: route change the blocker did not see, shell remount, closing the
 * workspace) pending edits are captured synchronously while the editor still exists and saved
 * once more if the session is still valid (`canSaveOnUnmount`, default: a non-expired Supabase
 * session). No retries or follow-up saves are scheduled after unmount.
 *
 * Transient failures retry with exponential backoff (8s doubling, capped at 60s) and keep
 * retrying at the 60s ceiling for as long as the page is open, so autosave recovers on its own
 * when the server comes back; edits, tab-hide and unmount still attempt saves meanwhile. Only an
 * error `isPermanentError` recognises (record gone, no permission, signed out) halts autosave:
 * then nothing saves automatically until `saveNow()` (an explicit save) succeeds. Edits made
 * meanwhile stay dirty and are guarded on unload.
 *
 * Status values: 'idle' | 'dirty' | 'saving' | 'saved' | 'error' | 'offline' | 'conflict'.
 *
 * @param {{getContent: () => string|null, save: (content: string) => Promise<void>, delay?: number, enabled?: boolean, isConflictError?: (error: unknown) => boolean, isPermanentError?: (error: unknown) => boolean, canSaveOnUnmount?: () => boolean|Promise<boolean>}} options - Options.
 * @returns {{status: string, lastSavedAt: number|null, error: string|null, markDirty: () => void, saveNow: () => Promise<boolean>, setBaseline: (content: string) => void, isDirty: () => boolean, clearConflict: () => void, isHalted: () => boolean}} Autosave API.
 */
export default function useAutosave({
  getContent,
  save,
  delay = 1500,
  enabled = true,
  isConflictError = () => false,
  isPermanentError = () => false,
  canSaveOnUnmount = hasActiveSession,
}) {
  const [status, setStatus] = useState('idle');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);
  const retryRef = useRef(null);
  const dirtyRef = useRef(false);
  const savingRef = useRef(null);
  const baselineRef = useRef(null);
  const conflictRef = useRef(false);
  /** Autosave stopped after a permanent error or too many failures (an explicit save resumes it). */
  const haltedRef = useRef(false);
  const failuresRef = useRef(0);
  const statusRef = useRef('idle');
  const mountedRef = useRef(true);
  const optsRef = useRef({ getContent, save, isConflictError, isPermanentError, enabled, delay, canSaveOnUnmount });
  optsRef.current = { getContent, save, isConflictError, isPermanentError, enabled, delay, canSaveOnUnmount };

  const setStatusBoth = useCallback((next) => {
    statusRef.current = next;
    if (mountedRef.current) setStatus(next);
  }, []);

  const setErrorSafe = useCallback((next) => {
    if (mountedRef.current) setError(next);
  }, []);

  /**
   * Save `snapshot` (or the editor's current content). Waits for any in-flight save first and
   * skips when nothing changed since the last successful save.
   * @param {string} [snapshot] - Content captured earlier (the unmount flush); read live when omitted.
   * @param {{manual?: boolean, ignoreBaseline?: boolean}} [opts] - An explicit save (resumes a
   *   halted autosave); write even when the content equals the last saved one (forced overwrite).
   * @returns {Promise<boolean>} True when the content is saved.
   */
  const runSave = useCallback(async (snapshot, { manual = false, ignoreBaseline = false } = {}) => {
    clearTimeout(timerRef.current);
    clearTimeout(retryRef.current);
    if (conflictRef.current) return false;
    if (haltedRef.current && !manual) return false;
    // Loop: another waiter may have started a new save while this one waited.
    while (savingRef.current) {
      await savingRef.current.catch(() => {});
    }
    if (conflictRef.current) return false;
    let content = snapshot;
    if (content === undefined) {
      try {
        content = optsRef.current.getContent();
      } catch {
        content = null;
      }
    }
    if (content == null) return false;
    if (!ignoreBaseline && baselineRef.current !== null && content === baselineRef.current) {
      dirtyRef.current = false;
      if (statusRef.current === 'dirty') setStatusBoth('saved');
      return true;
    }
    setStatusBoth('saving');
    setErrorSafe(null);
    const attempt = (async () => {
      await optsRef.current.save(content);
    })();
    savingRef.current = attempt;
    try {
      await attempt;
      baselineRef.current = content;
      failuresRef.current = 0;
      haltedRef.current = false;
      if (!mountedRef.current) {
        dirtyRef.current = false;
        return true;
      }
      // Edits made while saving keep the document dirty.
      let latest = content;
      try {
        latest = optsRef.current.getContent();
      } catch {
        latest = content;
      }
      dirtyRef.current = latest != null && latest !== content;
      setLastSavedAt(Date.now());
      setStatusBoth(dirtyRef.current ? 'dirty' : 'saved');
      if (dirtyRef.current) {
        timerRef.current = setTimeout(() => { runSave(); }, optsRef.current.delay);
      }
      return true;
    } catch (err) {
      if (optsRef.current.isConflictError(err)) {
        conflictRef.current = true;
        setErrorSafe(err?.message || 'Changed elsewhere');
        setStatusBoth('conflict');
        return false;
      }
      const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
      setErrorSafe(err?.message || 'Save failed');
      setStatusBoth(offline ? 'offline' : 'error');
      let permanent = false;
      try {
        permanent = Boolean(optsRef.current.isPermanentError(err));
      } catch {
        permanent = false;
      }
      failuresRef.current += 1;
      if (permanent) {
        haltedRef.current = true;
      } else if (mountedRef.current) {
        retryRef.current = setTimeout(() => {
          if (mountedRef.current) runSave();
        }, autosaveRetryDelay(failuresRef.current, offline));
      }
      return false;
    } finally {
      if (savingRef.current === attempt) savingRef.current = null;
    }
  }, [setStatusBoth, setErrorSafe]);

  /**
   * Perform a save now (skips when nothing changed unless `force`; waits for an in-flight save first).
   * @param {{force?: boolean}} [opts] - Write even if the content matches the last saved version.
   * @returns {Promise<boolean>} True when the content is saved.
   */
  const saveNow = useCallback(
    ({ force = false } = {}) => runSave(undefined, { manual: true, ignoreBaseline: force }),
    [runSave]
  );

  /** Mark content as changed and schedule an autosave. */
  const markDirty = useCallback(() => {
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      if (statusRef.current !== 'saving' && statusRef.current !== 'conflict') setStatusBoth('dirty');
    }
    if (!optsRef.current.enabled || conflictRef.current || haltedRef.current || !mountedRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { runSave(); }, optsRef.current.delay);
  }, [runSave, setStatusBoth]);

  /**
   * Set the saved baseline (e.g. right after load) without writing.
   * @param {string} content - Content considered saved.
   * @returns {void}
   */
  const setBaseline = useCallback((content) => {
    baselineRef.current = content;
    dirtyRef.current = false;
    haltedRef.current = false;
    failuresRef.current = 0;
    setStatusBoth('saved');
  }, [setStatusBoth]);

  /** Leave the conflict state (after reload or overwrite). */
  const clearConflict = useCallback(() => {
    conflictRef.current = false;
    setErrorSafe(null);
    setStatusBoth(dirtyRef.current ? 'dirty' : 'saved');
  }, [setStatusBoth, setErrorSafe]);

  const isDirty = useCallback(() => dirtyRef.current || Boolean(savingRef.current), []);

  // Save when the tab is hidden; warn before unload with unsaved work.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden' && dirtyRef.current && optsRef.current.enabled && !haltedRef.current) runSave();
    };
    const onBeforeUnload = (event) => {
      if (!dirtyRef.current && !savingRef.current) return undefined;
      if (optsRef.current.enabled && !conflictRef.current && !haltedRef.current) runSave();
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
      return '';
    };
    const onOnline = () => {
      if (statusRef.current === 'offline') runSave();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('online', onOnline);
    };
  }, [runSave]);

  // Final save on unmount. A layout-effect cleanup runs before the children's (the editor's
  // imperative handle is still attached and its document not yet destroyed), so the pending
  // content can be read synchronously here; the write itself then runs after unmount.
  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
      clearTimeout(retryRef.current);
      const opts = optsRef.current;
      if (!opts.enabled || conflictRef.current || haltedRef.current || !dirtyRef.current) return;
      let snapshot = null;
      try {
        snapshot = opts.getContent();
      } catch {
        snapshot = null;
      }
      if (snapshot == null || snapshot === baselineRef.current) return;
      Promise.resolve()
        .then(() => (typeof opts.canSaveOnUnmount === 'function' ? opts.canSaveOnUnmount() : true))
        .then((allowed) => (allowed ? runSave(snapshot) : false))
        .catch((err) => {
          console.warn('Final save on close failed:', err);
        });
    };
  }, [runSave]);

  /** Whether autosave stopped after a permanent error, until an explicit save succeeds. */
  const isHalted = useCallback(() => haltedRef.current, []);

  return { status, lastSavedAt, error, markDirty, saveNow, setBaseline, isDirty, clearConflict, isHalted };
}
